import { useCallback, useEffect, useMemo, useState } from 'react';
import {Alert, App as AntApp, Badge, Button, Card, Col, DatePicker, Empty, Flex, Form, Grid, Input, Layout, Row, Select, Space, Spin, Switch, Tag, Tooltip, Typography, theme as antdTheme,} from 'antd';
import {BulbFilled, BulbOutlined, CheckCircleFilled, CloseCircleFilled, DeleteOutlined, DisconnectOutlined, EditOutlined, PlusOutlined, QuestionCircleFilled, ReloadOutlined, WarningFilled,} from '@ant-design/icons';
import dayjs, { type Dayjs } from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@shared/contexts/ThemeContext';
import LanguageSwitcher from '@shared/components/LanguageSwitcher';
import { createMessage, deleteMessage, getAuthMe, getStatus, logout as apiLogout, updateMessage } from './api';
import LoginModal from './components/LoginModal';
import type {DetailsStatusResponse, HostInfo, HostSummary, MessagePayload, PublicStatusResponse, ServiceDetails, ServiceState, ServiceSummary, StatusKind, StatusMessageDto,} from './types';

const { Header, Content } = Layout;
const { Title, Text, Paragraph } = Typography;
const { useBreakpoint } = Grid;
const REFRESH_MS = 30_000;

function statusBadgeStatus(s: StatusKind): 'success' | 'warning' | 'error' | 'default' {
  switch (s) {
    case 'operational': return 'success';
    case 'degraded': return 'warning';
    case 'outage': return 'error';
    default: return 'default';
  }
}

function statusAlertType(s: StatusKind): 'success' | 'warning' | 'error' | 'info' {
  switch (s) {
    case 'operational': return 'success';
    case 'degraded': return 'warning';
    case 'outage': return 'error';
    default: return 'info';
  }
}

// Group names arrive as free-text from the API (e.g. "Core Services"). Normalize to a
// stable slug so it can key into `status:groups.*`; unknown groups fall back to the raw name.
function groupKey(group: string): string {
  return group.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
}

// Per-service state, falling back to the legacy healthy/hasData booleans if the API
// (an older backend) didn't send an explicit status.
function serviceState(svc: ServiceSummary): ServiceState {
  if (svc.status) return svc.status;
  if (!svc.hasData) return 'unknown';
  return svc.healthy ? 'operational' : 'down';
}

function serviceStateBadge(s: ServiceState): 'success' | 'warning' | 'error' | 'default' {
  switch (s) {
    case 'operational': return 'success';
    case 'degraded': return 'warning';
    // A host we cannot reach is, from the visitor's point of view, indistinguishable from down.
    case 'down': case 'unreachable': return 'error';
    default: return 'default';
  }
}

// A unique row key: the same service name legitimately exists on more than one host
// ("Database" is both the primary and the backup replica).
function serviceRowKey(svc: ServiceSummary): string {
  return `${svc.host ?? ''}/${svc.name}`;
}

function aggregateGroup(services: ServiceSummary[]): StatusKind {
  // Services flagged as not counting toward disruption are shown but never drag the
  // group status down (undefined => counts, for back-compat with older backends).
  const counting = services.filter(s => s.countsTowardOverall !== false);
  if (!counting.length) return 'operational';
  const states = counting.map(serviceState);
  const down = states.filter(s => s === 'down' || s === 'unknown' || s === 'unreachable').length;
  const degraded = states.filter(s => s === 'degraded').length;
  if (down === states.length) return 'outage';
  if (down > 0 || degraded > 0) return 'degraded';
  return 'operational';
}

function ServiceDot({ state }: { state: ServiceState }) {
  const { token } = antdTheme.useToken();
  switch (state) {
    case 'operational': return <CheckCircleFilled style={{ color: token.colorSuccess, fontSize: 16 }} />;
    case 'degraded': return <WarningFilled style={{ color: token.colorWarning, fontSize: 16 }} />;
    case 'down': return <CloseCircleFilled style={{ color: token.colorError, fontSize: 16 }} />;
    case 'unreachable': return <DisconnectOutlined style={{ color: token.colorError, fontSize: 16 }} />;
    default: return <QuestionCircleFilled style={{ color: token.colorTextDisabled, fontSize: 16 }} />;
  }
}

// Which host probed the service. Only worth showing once more than one host reports in.
function HostTag({ svc }: { svc: ServiceSummary }) {
  const { t } = useTranslation(['status']);
  const label = svc.hostDisplayName ?? svc.host;
  if (!label) return null;
  return (
    <Tooltip title={t('status:host.tagTooltip', { host: label })}>
      <Tag>{label}</Tag>
    </Tooltip>
  );
}

// Public counterpart of HostBanner: says that part of the fleet is unreachable without naming it.
function PublicHostBanner({ summary }: { summary: HostSummary }) {
  const { t } = useTranslation(['status']);
  return (
    <Alert
      type="error"
      showIcon
      icon={<DisconnectOutlined />}
      title={t('status:host.publicUnreachableTitle')}
      description={t('status:host.publicUnreachableBody', { reachable: summary.reachable, total: summary.total })}
    />
  );
}

function HostBanner({ host }: { host: HostInfo }) {
  const { t } = useTranslation(['status']);
  return (
    <Alert
      type="error"
      showIcon
      icon={<DisconnectOutlined />}
      title={t('status:host.unreachableTitle', { host: host.displayName })}
      description={t('status:host.unreachableBody', {
        host: host.displayName,
        observer: host.observedBy,
        lastSeen: host.lastSeenAt ? dayjs(host.lastSeenAt).format('LLL') : '\u2014',
      })}
    />
  );
}

function PublicServiceRow({ svc, showHost }: { svc: ServiceSummary; showHost: boolean }) {
  const { token } = antdTheme.useToken();
  const { t } = useTranslation(['status', 'common']);
  const state = serviceState(svc);
  return (
    <Flex
      align="center"
      justify="space-between"
      style={{
        padding: `${token.paddingSM}px ${token.padding}px`,
        borderTop: `1px solid ${token.colorBorderSecondary}`,
      }}
    >
      <Space size="small">
        <ServiceDot state={state} />
        <Text>{svc.name}</Text>
        {showHost && <HostTag svc={svc} />}
        {svc.countsTowardOverall === false && (
          <Tooltip title={t('status:service.informationalTooltip', { defaultValue: 'Monitored for visibility only; does not affect overall status.' })}>
            <Tag>{t('status:service.informational', { defaultValue: 'Informational' })}</Tag>
          </Tooltip>
        )}
      </Space>
      <Space size="small">
        {svc.uptime != null && (
          <Text type="secondary" style={{ fontSize: 12, fontVariantNumeric: 'tabular-nums' }}>
            {t('status:service.uptime', { pct: svc.uptime })}
          </Text>
        )}
        <Tag color={serviceStateBadge(state)}>{t(`status:service.${state}`)}</Tag>
      </Space>
    </Flex>
  );
}

function RecentTicks({ recent }: { recent: ServiceDetails['recent'] }) {
  const { token } = antdTheme.useToken();
  const { t } = useTranslation(['status']);
  if (!recent || recent.length === 0) return null;
  const ordered = [...recent].slice().reverse();
  return (
    <Flex gap={3}>
      {ordered.map(c => {
        // Synthetic ticks were recorded because the owning host was unreachable, not because
        // anything actually probed the service. Dimmed and labelled so an admin reading the
        // history can tell an inferred outage from an observed one.
        const prefix = c.synthetic ? t('status:service.synthetic') + ' · ' : '';
        return (
          <Tooltip key={c.checkedAt} title={`${prefix}${dayjs(c.checkedAt).format('LLL')}${c.error ? ' · ' + c.error : ''}`}>
            <span
              style={{
                display: 'inline-block',
                width: 8,
                height: 18,
                borderRadius: 2,
                background: c.healthy ? token.colorSuccess : token.colorError,
                opacity: c.synthetic ? 0.35 : c.healthy ? 0.85 : 1,
              }}
            />
          </Tooltip>
        );
      })}
    </Flex>
  );
}

function AdminServiceRow({ svc, showHost }: { svc: ServiceDetails; showHost: boolean }) {
  const { token } = antdTheme.useToken();
  const { t } = useTranslation(['status']);
  const state = serviceState(svc);
  return (
    <div style={{ padding: `${token.paddingSM}px ${token.padding}px`, borderTop: `1px solid ${token.colorBorderSecondary}` }}>
      <Flex justify="space-between" align="center" gap="small" wrap="wrap">
        <Space size="small">
          <ServiceDot state={state} />
          <Text strong>{svc.displayName}</Text>
          {showHost && <HostTag svc={svc} />}
          {svc.isInternalOnly && <Tag>{t('status:admin.service.internal')}</Tag>}
          {svc.countsTowardOverall === false && (
            <Tooltip title={t('status:service.informationalTooltip', { defaultValue: 'Monitored for visibility only; does not affect overall status.' })}>
              <Tag>{t('status:service.informational', { defaultValue: 'Informational' })}</Tag>
            </Tooltip>
          )}
          <Text type="secondary" style={{ fontSize: 12 }}>{svc.name}</Text>
        </Space>
        <Space size="small">
          {svc.uptime != null && (
            <Text type="secondary" style={{ fontSize: 12, fontVariantNumeric: 'tabular-nums' }}>
              {t('status:service.uptime', { pct: svc.uptime })}
            </Text>
          )}
          {svc.responseTimeMs != null && (
            <Text type="secondary" style={{ fontVariantNumeric: 'tabular-nums' }}>
              {t('status:admin.service.responseMs', { ms: svc.responseTimeMs })}
            </Text>
          )}
          <Tag color={serviceStateBadge(state)}>{t(`status:service.${state}`)}</Tag>
        </Space>
      </Flex>
      {!svc.healthy && svc.lastError && (
        <Paragraph
          type="danger"
          style={{ marginTop: token.marginXS, marginBottom: 0, fontSize: 13 }}
          ellipsis={{ rows: 2, expandable: true, symbol: 'more' }}
        >
          {svc.lastError}
        </Paragraph>
      )}
      {state === 'degraded' && svc.degradedReason && (
        <Paragraph
          type="warning"
          style={{ marginTop: token.marginXS, marginBottom: 0, fontSize: 13 }}
        >
          {svc.degradedReason}
        </Paragraph>
      )}
      <div style={{ marginTop: token.marginXS }}>
        <RecentTicks recent={svc.recent} />
      </div>
    </div>
  );
}

// Picks the stored translation matching the visitor's language, falling back to the
// original (English) text when the language is English or the translation is missing.
function localizeMessage(msg: StatusMessageDto, lang: string): { title: string; body: string } {
  if (lang === 'en') return { title: msg.title, body: msg.body };
  const tr = msg.translations?.[lang];
  return tr?.title ? { title: tr.title, body: tr.body } : { title: msg.title, body: msg.body };
}

function MessageBanner({ msg }: { msg: StatusMessageDto }) {
  const { t, i18n } = useTranslation(['status', 'common']);
  const { title, body } = localizeMessage(msg, i18n.language);
  const type = msg.type === 'incident' ? 'error' : msg.type === 'maintenance' ? 'warning' : 'info';
  const range = [
    msg.startsAt ? t('status:message.startsAt', { time: dayjs(msg.startsAt).format('lll') }) : null,
    msg.endsAt ? t('status:message.endsAt', { time: dayjs(msg.endsAt).format('lll') }) : null,
  ].filter(Boolean).join(' · ');
  return (
    <Alert
      type={type}
      showIcon
      banner={false}
      title={
        <Flex justify="space-between" align="center" gap="small" wrap="wrap">
          <Text strong>{title}</Text>
          <Space size="small">
            <Tag>{t(`status:message.type.${msg.type}`)}</Tag>
            <Tag color={msg.severity === 'critical' ? 'red' : msg.severity === 'major' ? 'orange' : 'gold'}>
              {t(`status:message.severity.${msg.severity}`)}
            </Tag>
          </Space>
        </Flex>
      }
      description={
        <>
          <Paragraph style={{ marginBottom: range ? 4 : 0, whiteSpace: 'pre-wrap' }}>{body}</Paragraph>
          {range && <Text type="secondary" style={{ fontSize: 12 }}>{range}</Text>}
        </>
      }
    />
  );
}

interface MessageFormValues {
  title: string;
  body: string;
  type: StatusMessageDto['type'];
  severity: StatusMessageDto['severity'];
  range?: [Dayjs | null, Dayjs | null] | null;
  isActive: boolean;
  openRouterKey?: string;
}

function MessageEditor({
  open,
  initial,
  onSave,
  onCancel,
}: {
  open: boolean;
  initial: StatusMessageDto | null;
  onSave: (values: MessagePayload) => Promise<void>;
  onCancel: () => void;
}) {
  const { t } = useTranslation(['status', 'common']);
  const [form] = Form.useForm<MessageFormValues>();
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    form.setFieldsValue({
      title: initial?.title ?? '',
      body: initial?.body ?? '',
      type: initial?.type ?? 'info',
      severity: initial?.severity ?? 'minor',
      range: initial?.startsAt || initial?.endsAt
        ? [initial?.startsAt ? dayjs(initial.startsAt) : null, initial?.endsAt ? dayjs(initial.endsAt) : null]
        : null,
      isActive: initial?.isActive ?? true,
      // Never prefilled: the key is pasted fresh per save and never stored anywhere.
      openRouterKey: '',
    });
  }, [open, initial, form]);

  if (!open) return null;

  const handleSubmit = async () => {
    const values = await form.validateFields();
    setSubmitting(true);
    try {
      await onSave({
        title: values.title.trim(),
        body: values.body.trim(),
        type: values.type,
        severity: values.severity,
        startsAt: values.range?.[0]?.toISOString() ?? null,
        endsAt: values.range?.[1]?.toISOString() ?? null,
        isActive: values.isActive,
        openRouterKey: values.openRouterKey?.trim() || undefined,
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card
      size="small"
      title={initial ? t('status:admin.editor.titleEdit') : t('status:admin.editor.titleNew')}
      style={{ marginBottom: 16 }}
    >
      <Form form={form} layout="vertical">
        <Form.Item label={t('status:admin.editor.fields.title')} name="title" rules={[{ required: true, message: t('status:admin.editor.validation.titleRequired') }]}>
          <Input placeholder={t('status:admin.editor.placeholders.title')} />
        </Form.Item>
        <Form.Item label={t('status:admin.editor.fields.body')} name="body" rules={[{ required: true, message: t('status:admin.editor.validation.bodyRequired') }]}>
          <Input.TextArea rows={3} placeholder={t('status:admin.editor.placeholders.body')} />
        </Form.Item>
        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <Form.Item label={t('status:admin.editor.fields.type')} name="type" rules={[{ required: true }]}>
              <Select
                options={[
                  { value: 'info', label: t('status:message.type.info') },
                  { value: 'maintenance', label: t('status:message.type.maintenance') },
                  { value: 'incident', label: t('status:message.type.incident') },
                ]}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item label={t('status:admin.editor.fields.severity')} name="severity" rules={[{ required: true }]}>
              <Select
                options={[
                  { value: 'minor', label: t('status:message.severity.minor') },
                  { value: 'major', label: t('status:message.severity.major') },
                  { value: 'critical', label: t('status:message.severity.critical') },
                ]}
              />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item label={t('status:admin.editor.fields.window')} name="range">
          <DatePicker.RangePicker showTime style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item label={t('status:admin.editor.fields.active')} name="isActive" valuePropName="checked">
          <Switch />
        </Form.Item>
        <Form.Item
          label={t('status:admin.editor.fields.openRouterKey')}
          name="openRouterKey"
          extra={t('status:admin.editor.openRouterKeyHelp')}
        >
          <Input.Password placeholder={t('status:admin.editor.placeholders.openRouterKey')} autoComplete="off" />
        </Form.Item>
        <Flex gap="small">
          <Button type="primary" loading={submitting} onClick={handleSubmit}>{t('status:admin.editor.save')}</Button>
          <Button onClick={onCancel}>{t('status:admin.editor.cancel')}</Button>
        </Flex>
      </Form>
    </Card>
  );
}

function App() {
  const { t } = useTranslation(['status', 'common']);
  const { themeMode, toggleTheme } = useTheme();
  const { token } = antdTheme.useToken();
  const screens = useBreakpoint();
  const { message } = AntApp.useApp();

  const [loading, setLoading] = useState(true);
  const [details, setDetails] = useState<DetailsStatusResponse | null>(null);
  const [publicView, setPublicView] = useState<PublicStatusResponse | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [googleClientId, setGoogleClientId] = useState('');
  const [loginOpen, setLoginOpen] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState<StatusMessageDto | null>(null);

  const refresh = useCallback(async () => {
    try {
      const r = await getStatus();
      setIsAdmin(r.isAdmin);
      setDetails(r.details ?? null);
      setPublicView(r.publicView ?? null);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
    const id = setInterval(refresh, REFRESH_MS);
    return () => clearInterval(id);
  }, [refresh]);

  useEffect(() => {
    getAuthMe().then(me => setGoogleClientId(me.googleClientId)).catch(() => {});
  }, []);

  const handleLoginSuccess = useCallback(async () => {
    setLoginOpen(false);
    message.success(t('common:auth.signedIn'));
    await refresh();
  }, [message, refresh, t]);

  const handleLogout = useCallback(async () => {
    try {
      await apiLogout();
      message.success(t('common:auth.signedOut'));
      await refresh();
    } catch (e) {
      console.error(e);
    }
  }, [message, refresh, t]);

  const data = details ?? publicView;
  const overall = data?.overall ?? 'unknown';
  // Named hosts only exist in the admin details response; the public payload carries an
  // anonymous count. Derive the same summary from either so the header line is uniform.
  const hosts = details?.hosts ?? [];
  const hostSummary: HostSummary | undefined = details
    ? (hosts.length ? { total: hosts.length, reachable: hosts.filter(h => h.reachable).length } : undefined)
    : publicView?.hostSummary;
  // Single-host deployments (the dev mono) should look exactly as they did before federation.
  const multiHost = (hostSummary?.total ?? 0) > 1;
  const showHost = isAdmin && hosts.length > 1;
  const unreachableHosts = hosts.filter(h => !h.reachable);
  const publicOutage = !details && multiHost && hostSummary!.reachable < hostSummary!.total;
  const messages = data?.messages ?? [];
  const visibleMessages = isAdmin ? messages : messages.filter(m => m.isActive !== false);
  const asOf = data?.asOf;

  const publicGroups = useMemo(() => {
    if (publicView) return publicView.groups;
    if (details) {
      return details.groups.map(g => ({
        group: g.group,
        status: aggregateGroup(g.services),
        services: g.services as unknown as ServiceSummary[],
      }));
    }
    return [];
  }, [publicView, details]);

  const handleSaveMessage = async (payload: MessagePayload) => {
    try {
      if (editing) {
        await updateMessage(editing.id, payload);
        message.success(t('status:admin.messages.toast.updated'));
      } else {
        await createMessage(payload);
        message.success(t('status:admin.messages.toast.created'));
      }
      setEditorOpen(false);
      setEditing(null);
      await refresh();
    } catch (e) {
      message.error(t('status:admin.messages.toast.saveFailed'));
      console.error(e);
    }
  };

  const handleDelete = async (msg: StatusMessageDto) => {
    if (!window.confirm(t('status:admin.messages.confirmDelete', { title: msg.title }))) return;
    try {
      await deleteMessage(msg.id);
      message.success(t('status:admin.messages.toast.deleted'));
      await refresh();
    } catch (e) {
      message.error(t('status:admin.messages.toast.deleteFailed'));
      console.error(e);
    }
  };

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Header
        style={{
          padding: `0 ${token.paddingLG}px`,
          background: token.colorBgContainer,
          borderBottom: `1px solid ${token.colorBorderSecondary}`,
          height: 80,
          lineHeight: 'normal',
          display: 'flex',
          alignItems: 'center',
          position: 'sticky',
          top: 0,
          zIndex: 50,
        }}
      >
        <Flex align="center" justify="space-between" style={{ width: '100%' }} gap="small" wrap="wrap">
          <Flex align="center" gap="middle">
            <Title level={screens.md ? 3 : 4} style={{ margin: 0 }}>{t('status:header.title')}</Title>
            <Badge status={statusBadgeStatus(overall)} text={t(`status:overall.${overall}`)} />
          </Flex>
          <Space size="small">
            <LanguageSwitcher />
            <Tooltip title={t('status:actions.refreshTooltip')}>
              <Button icon={<ReloadOutlined />} onClick={refresh} />
            </Tooltip>
            <Tooltip title={themeMode === 'light' ? t('status:actions.switchToDark') : t('status:actions.switchToLight')}>
              <Button
                icon={themeMode === 'light' ? <BulbOutlined /> : <BulbFilled />}
                onClick={toggleTheme}
              />
            </Tooltip>
            {isAdmin ? (
              <Button onClick={handleLogout}>{t('common:auth.signOut')}</Button>
            ) : (
              <Button
                type="primary"
                onClick={() => setLoginOpen(true)}
                disabled={!googleClientId}
              >
                {t('common:auth.signIn')}
              </Button>
            )}
          </Space>
        </Flex>
      </Header>

      <Content style={{ padding: token.paddingLG, maxWidth: 1100, margin: '0 auto', width: '100%' }}>
        <Spin spinning={loading && !data}>
          {data && (
            <Alert
              type={statusAlertType(overall)}
              showIcon
              icon={overall === 'operational' ? <CheckCircleFilled /> : overall === 'unknown' ? <QuestionCircleFilled /> : <WarningFilled />}
              title={t(`status:overall.${overall}`)}
              description={
                asOf || multiHost ? (
                  <Space size="small" wrap>
                    {asOf && <span>{t('status:alert.asOf', { time: dayjs(asOf).format('LLL') })}</span>}
                    {multiHost && hostSummary && (
                      <Text type="secondary">
                        {t('status:host.summary', { reachable: hostSummary.reachable, total: hostSummary.total })}
                      </Text>
                    )}
                  </Space>
                ) : undefined
              }
              style={{ marginBottom: token.margin }}
            />
          )}

          {visibleMessages.length > 0 && (
            <Space orientation="vertical" size="middle" style={{ width: '100%', marginBottom: token.margin }}>
              {visibleMessages.map(m => <MessageBanner key={m.uid ?? m.id} msg={m} />)}
            </Space>
          )}

          {unreachableHosts.length > 0 && (
            <Space orientation="vertical" size="middle" style={{ width: '100%', marginBottom: token.margin }}>
              {unreachableHosts.map(h => <HostBanner key={h.name} host={h} />)}
            </Space>
          )}

          {publicOutage && hostSummary && (
            <div style={{ marginBottom: token.margin }}>
              <PublicHostBanner summary={hostSummary} />
            </div>
          )}

          <Row gutter={[token.margin, token.margin]}>
            {publicGroups.map(g => {
              const detailGroup = details?.groups.find(x => x.group === g.group);
              return (
                <Col xs={24} key={g.group}>
                  <Card
                    title={t(`status:groups.${groupKey(g.group)}`, { defaultValue: g.group })}
                    extra={<Tag color={statusBadgeStatus(g.status)}>{t(`status:groupStatus.${g.status}`)}</Tag>}
                    styles={{ body: { padding: 0 } }}
                  >
                    {detailGroup
                      ? detailGroup.services.map(s => (
                          <AdminServiceRow key={serviceRowKey(s)} svc={s} showHost={showHost} />
                        ))
                      : g.services.map((s, i) => (
                          // Public rows carry no host, so a same-named service on two hosts needs the index.
                          <PublicServiceRow key={`${i}/${s.name}`} svc={s} showHost={showHost} />
                        ))}
                  </Card>
                </Col>
              );
            })}
          </Row>

          {isAdmin && (
            <Card
              style={{ marginTop: token.marginLG }}
              title={t('status:admin.messages.sectionTitle')}
              extra={
                !editorOpen && (
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => { setEditing(null); setEditorOpen(true); }}
                  >
                    {t('status:admin.messages.new')}
                  </Button>
                )
              }
            >
              {editorOpen && (
                <MessageEditor
                  open={editorOpen}
                  initial={editing}
                  onSave={handleSaveMessage}
                  onCancel={() => { setEditorOpen(false); setEditing(null); }}
                />
              )}
              {messages.length === 0 ? (
                <Empty description={t('status:admin.messages.empty')} />
              ) : (
                <Space orientation="vertical" size="small" style={{ width: '100%' }}>
                  {messages.map(m => (
                    <Card key={m.id} size="small" styles={{ body: { padding: token.padding } }}>
                      <Flex justify="space-between" align="flex-start" gap="small" wrap="wrap">
                        <div style={{ flex: 1, minWidth: 240 }}>
                          <Space size="small" style={{ marginBottom: token.marginXXS }}>
                            <Text strong>{m.title}</Text>
                            <Tag color={m.isActive ? 'green' : 'default'}>{m.isActive ? t('status:admin.messages.active') : t('status:admin.messages.inactive')}</Tag>
                            <Tag>{t(`status:message.type.${m.type}`)}</Tag>
                            <Tag color={m.severity === 'critical' ? 'red' : m.severity === 'major' ? 'orange' : 'gold'}>
                              {t(`status:message.severity.${m.severity}`)}
                            </Tag>
                          </Space>
                          <Paragraph style={{ marginBottom: 4, whiteSpace: 'pre-wrap' }}>{m.body}</Paragraph>
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            {t('status:admin.messages.createdBy', { user: m.createdBy ?? t('status:admin.messages.unknownUser') })}
                            {m.createdAt ? ' · ' + dayjs(m.createdAt).format('LLL') : ''}
                          </Text>
                        </div>
                        <Space size="small">
                          <Button
                            size="small"
                            icon={<EditOutlined />}
                            onClick={() => { setEditing(m); setEditorOpen(true); }}
                          >
                            {t('status:admin.messages.edit')}
                          </Button>
                          <Button
                            size="small"
                            danger
                            icon={<DeleteOutlined />}
                            onClick={() => handleDelete(m)}
                          >
                            {t('status:admin.messages.delete')}
                          </Button>
                        </Space>
                      </Flex>
                    </Card>
                  ))}
                </Space>
              )}
            </Card>
          )}
        </Spin>
      </Content>

      <LoginModal
        open={loginOpen}
        clientId={googleClientId}
        onCancel={() => setLoginOpen(false)}
        onSuccess={handleLoginSuccess}
      />
    </Layout>
  );
}

export default App;
